import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import { useTestMode } from '../lib/testModeContext.jsx';
import { useTripJson } from '../hooks/data.js';
import { fetchMealVotes, setMealVote, resolveMealSlot } from '../lib/mealsApi.js';
import { openStatus, summarizeWeek } from '../lib/restaurantHours.js';

// Restaurants are flat (restaurants.json v2). Each restaurant declares
// the meal-slots it fits via `meal_slots`. We render two views:
//   • By Meal — collapsed to four meal-type buckets (Coffee /
//     Breakfast / Lunch / Dinner) instead of date-anchored slots.
//     The trip is short and most restaurants serve multiple slots,
//     so date-axis grouping was duplicative. Voting + 'mark eaten'
//     map to the bucket's currently-selected slot (defaults to next
//     un-resolved; chip selector lets the user pick another).
//   • By Type — category-grouped (sit-down / brewpub / etc.).
//     Read-only browse, no voting context.
//
// Both views support an "open now only" filter using the trip-tz clock
// against each restaurant's hours_structured. iOS Safari tabs can have
// stale clocks if the device went to sleep — we re-query useTestMode's
// `now()` once per minute via setInterval to keep the open-status
// chips fresh.

const CATEGORY_ORDER = [
  { id: 'coffee', label: 'Coffee shops' },
  { id: 'sit_down', label: 'Sit-down restaurants' },
  { id: 'casual', label: 'Casual / counter' },
  { id: 'food_truck', label: 'Food trucks' },
  { id: 'brewpub', label: 'Brewpubs' },
  { id: 'cocktail_bar', label: 'Cocktail bars' },
  { id: 'late_night', label: 'Late-night' },
  { id: 'seafood', label: 'Seafood-led' },
  { id: 'upscale', label: 'Upscale' },
  { id: 'bakery', label: 'Bakeries' },
];

// 4 By-Meal buckets, in display order. Coffee shops surface alone in
// the Coffee bucket — they're not duplicated into Breakfast even if
// their meal_slots include breakfast slots. Non-coffee restaurants
// appear in any bucket whose meal-type they tag.
const MEAL_BUCKETS = [
  { id: 'coffee', label: 'Coffee', slotType: 'breakfast' },
  { id: 'breakfast', label: 'Breakfast', slotType: 'breakfast' },
  { id: 'lunch', label: 'Lunch', slotType: 'lunch' },
  { id: 'dinner', label: 'Dinner', slotType: 'dinner' },
];

function isCoffeeShop(r) {
  const cats = Array.isArray(r.category) ? r.category : [r.category].filter(Boolean);
  return cats.includes('coffee');
}

function restaurantHasSlotType(r, slotType, slots) {
  // Collect ids of all slots matching the requested meal type
  const slotIds = new Set(slots.filter((s) => s.type === slotType).map((s) => s.id));
  return (r.meal_slots || []).some((id) => slotIds.has(id));
}

function bucketContains(bucket, restaurant, slots) {
  if (bucket.id === 'coffee') return isCoffeeShop(restaurant);
  if (isCoffeeShop(restaurant)) return false; // coffee stays in Coffee bucket only
  return restaurantHasSlotType(restaurant, bucket.slotType, slots);
}

const VEG_TONE = {
  excellent: 'border-success/40 bg-success/10 text-success',
  good: 'border-success/30 bg-success/5 text-success',
  decent: 'border-copper/30 bg-copper/5 text-copper-dark',
  limited: 'border-copper/40 bg-copper/10 text-copper-dark',
  poor: 'border-danger/30 bg-danger/5 text-danger',
};

export default function Meals() {
  const { tripId, trip } = useTrip();
  const session = useSession();
  const testMode = useTestMode();
  const data = useTripJson(tripId, 'restaurants.json');
  const [votes, setVotes] = useState({});
  const [resolved, setResolved] = useState({});
  const [error, setError] = useState(null);
  const [pendingSlot, setPendingSlot] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [view, setView] = useState('by_meal'); // 'by_meal' | 'by_type'
  const [openNowOnly, setOpenNowOnly] = useState(false);
  const [now, setNow] = useState(() => testMode?.now?.() || new Date());
  // Per-bucket selected slot id. Defaults to next un-resolved (computed
  // below). Empty string means "let the default win".
  const [bucketSlotPick, setBucketSlotPick] = useState({});

  // Keep open-status chips fresh once a minute.
  useEffect(() => {
    const id = setInterval(() => setNow(testMode?.now?.() || new Date()), 60_000);
    return () => clearInterval(id);
  }, [testMode]);

  const tz = trip?.dates?.timezone || 'America/Juneau';

  const refresh = useCallback(async () => {
    try {
      const res = await fetchMealVotes({ tripId });
      setVotes(res.votes || {});
      setResolved(res.resolved || {});
      setError(null);
    } catch (err) {
      if (err.status === 'api_unavailable') {
        setVotes({});
        setResolved({});
        setError(null);
        return;
      }
      setError(err.message || String(err));
    }
  }, [tripId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const meId = session?.role === 'player' ? session.recipient_id : null;
  const players = trip?.players || [];

  const onVote = useCallback(
    async (slotId, optionId) => {
      if (!meId) return;
      const current = votes[slotId]?.[meId];
      const next = current === optionId ? null : optionId;
      setPendingSlot(slotId);
      try {
        const res = await setMealVote({ tripId, slotId, optionId: next });
        setVotes((prev) => ({ ...prev, [slotId]: res.votes || {} }));
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setPendingSlot(null);
      }
    },
    [tripId, meId, votes]
  );

  const onResolve = useCallback(
    async (slotId, optionId) => {
      if (!meId) return;
      setPendingSlot(slotId);
      try {
        const res = await resolveMealSlot({ tripId, slotId, optionId });
        setResolved((prev) => ({ ...prev, [slotId]: res.resolved || null }));
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setPendingSlot(null);
      }
    },
    [tripId, meId]
  );

  // Stable refs so the useMemo dep arrays below don't re-run every
  // render — without this, the empty-array fallback gets a fresh
  // identity on each render and React's exhaustive-deps lint complains.
  const restaurants = useMemo(() => data.data?.restaurants || [], [data.data]);
  const slots = useMemo(() => data.data?.slots || [], [data.data]);

  // Apply the open-now filter to restaurant lists.
  const filterByOpen = useCallback(
    (list) => {
      if (!openNowOnly) return list;
      return list.filter((r) => openStatus(r.hours_structured, now, tz).open);
    },
    [openNowOnly, now, tz]
  );

  // 4 meal-type buckets. Each carries its own restaurant list, all
  // applicable trip slots of that meal-type (chronological), the
  // currently-selected slot for voting/mark-eaten (defaulting to the
  // next un-resolved), and a flag for whether all of its slots are
  // resolved.
  const mealBuckets = useMemo(() => {
    return MEAL_BUCKETS.map((bucket) => {
      const bucketRestaurants = filterByOpen(
        restaurants.filter((r) => bucketContains(bucket, r, slots))
      );
      // All trip slots that match this bucket's meal-type, sorted by
      // date_iso (chronological).
      const bucketSlots = slots
        .filter((s) => s.type === bucket.slotType)
        .slice()
        .sort((a, b) => (a.date_iso || '').localeCompare(b.date_iso || ''));
      // First un-resolved slot drives default voting target.
      const nextUnresolved = bucketSlots.find(
        (s) => !resolved[s.id]?.option_id
      );
      const picked = bucketSlotPick[bucket.id];
      // Use the picked slot if it's in the bucket and still un-resolved;
      // otherwise fall back to next-un-resolved; if all resolved, use
      // the last slot (so eaten history still has a meaningful key).
      const isPickedValid =
        picked &&
        bucketSlots.some((s) => s.id === picked) &&
        !resolved[picked]?.option_id;
      const activeSlot =
        (isPickedValid && bucketSlots.find((s) => s.id === picked)) ||
        nextUnresolved ||
        bucketSlots[bucketSlots.length - 1] ||
        null;
      const allResolved = bucketSlots.length > 0 && !nextUnresolved;
      return {
        ...bucket,
        restaurants: bucketRestaurants,
        slots: bucketSlots,
        activeSlot,
        allResolved,
      };
    }).filter((b) => b.restaurants.length > 0 || b.slots.length > 0);
  }, [restaurants, slots, resolved, filterByOpen, bucketSlotPick]);

  // Archive view: every resolved slot, regardless of bucket. Renders
  // the same as before so the existing per-slot eaten history surfaces.
  const archivedSlots = useMemo(() => {
    return slots
      .filter((s) => resolved[s.id]?.option_id)
      .map((slot) => ({ slot }));
  }, [slots, resolved]);

  // Category groups for By-Type view.
  const categoryGroups = useMemo(() => {
    const present = new Map(); // category_id → restaurants[]
    for (const r of filterByOpen(restaurants)) {
      const cats = Array.isArray(r.category) ? r.category : [r.category].filter(Boolean);
      for (const c of cats) {
        if (!present.has(c)) present.set(c, []);
        present.get(c).push(r);
      }
    }
    // Render in canonical order, only categories that have entries.
    return CATEGORY_ORDER.filter((c) => present.has(c.id)).map((c) => ({
      ...c,
      restaurants: present.get(c.id),
    }));
  }, [restaurants, filterByOpen]);

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          group decision · {restaurants.length} options
        </p>
        <h1 className="mt-1 font-serif text-2xl">Meals</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Per-meal candidates with Wilman&apos;s take. Tap to vote, mark eaten when the food&apos;s
          gone, or browse by type.
        </p>
      </header>

      {!session.loading && !meId && (
        <div className="card border-copper/40 bg-copper/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            view-only
          </p>
          <p className="mt-1 text-sm text-fg-secondary">
            Open from your briefing-email link to vote. You can browse the shortlists either way.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <ViewChip id="by_meal" current={view} onClick={setView} label="By Meal" />
        <ViewChip id="by_type" current={view} onClick={setView} label="By Type" />
        <span aria-hidden className="flex-1" />
        <button
          type="button"
          onClick={() => setOpenNowOnly((v) => !v)}
          className={[
            'rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition',
            openNowOnly
              ? 'border-action bg-action text-action-fg'
              : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
          ].join(' ')}
        >
          {openNowOnly ? 'open now ✓' : 'open now'}
        </button>
      </div>

      {data.loading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading restaurants
        </p>
      )}
      {data.error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          restaurants unavailable — {data.error.message}
        </div>
      )}

      {view === 'by_meal' && (
        <>
          {mealBuckets.length > 1 && <MealQuickJump buckets={mealBuckets} />}
          <ul className="space-y-3">
            {mealBuckets.map((bucket) => (
              <BucketCard
                key={bucket.id}
                anchorId={`meal-bucket-${bucket.id}`}
                bucket={bucket}
                resolved={resolved}
                onPickSlot={(slotId) =>
                  setBucketSlotPick((prev) => ({ ...prev, [bucket.id]: slotId }))
                }
                players={players}
                votes={bucket.activeSlot ? votes[bucket.activeSlot.id] || {} : {}}
                meId={meId}
                pending={
                  bucket.activeSlot ? pendingSlot === bucket.activeSlot.id : false
                }
                onVote={onVote}
                onResolve={onResolve}
                now={now}
                tz={tz}
              />
            ))}
            {mealBuckets.length === 0 && slots.length > 0 && (
              <li className="card px-4 py-6 text-center font-serif italic text-fg-secondary">
                {openNowOnly ? 'Nothing open right now.' : 'All meal slots resolved. Eat well.'}
              </li>
            )}
          </ul>

          {archivedSlots.length > 0 && (
            <section className="space-y-2">
              <button
                type="button"
                onClick={() => setShowArchive((v) => !v)}
                className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline"
              >
                {showArchive
                  ? 'hide eaten'
                  : `show eaten (${archivedSlots.length})`}
              </button>
              {showArchive && (
                <ul className="space-y-2">
                  {archivedSlots.map((entry) => (
                    <ArchivedSlot
                      key={entry.slot.id}
                      slot={entry.slot}
                      restaurants={restaurants}
                      resolved={resolved[entry.slot.id]}
                      onUndo={() => onResolve(entry.slot.id, null)}
                      canUndo={!!meId}
                    />
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}

      {view === 'by_type' && (
        <ul className="space-y-4">
          {categoryGroups.map((group) => (
            <li key={group.id} className="space-y-2">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
                {group.label} · {group.restaurants.length}
              </h2>
              <ul className="space-y-2">
                {group.restaurants.map((r) => (
                  <li key={r.id} className="card overflow-hidden">
                    <RestaurantBody restaurant={r} now={now} tz={tz} compact />
                    <p className="border-t border-border-tertiary bg-bg-secondary px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                      hours · <span className="normal-case tracking-normal text-fg-secondary">{summarizeWeek(r.hours_structured)}</span>
                    </p>
                  </li>
                ))}
              </ul>
            </li>
          ))}
          {categoryGroups.length === 0 && (
            <li className="card px-4 py-6 text-center font-serif italic text-fg-secondary">
              {openNowOnly ? 'Nothing open right now.' : 'No restaurants in feed.'}
            </li>
          )}
        </ul>
      )}

      {data.data?.notes && (
        <p className="px-1 text-xs italic text-fg-tertiary">{data.data.notes}</p>
      )}
    </div>
  );
}

function ViewChip({ id, current, onClick, label }) {
  const selected = current === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={[
        'rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition',
        selected
          ? 'border-action bg-action text-action-fg'
          : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

// Sticky quick-jump bar — 4 icon buttons across the iPhone width that
// scroll to the matching bucket section. Uses scroll-mt-20 on the
// targets so the destination header lands clear of this sticky bar.
function MealQuickJump({ buckets }) {
  const handleJump = (bucketId) => {
    const el = document.getElementById(`meal-bucket-${bucketId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <nav
      aria-label="Jump to meal type"
      className="sticky top-0 z-20 -mx-4 border-y border-border-tertiary bg-bg-primary/95 px-4 py-2 backdrop-blur"
    >
      <ul className="flex items-stretch justify-around gap-1">
        {buckets.map((b) => (
          <li key={b.id} className="flex-1">
            <button
              type="button"
              onClick={() => handleJump(b.id)}
              className="group flex w-full flex-col items-center gap-0.5 rounded-md py-1 transition hover:bg-bg-tertiary"
              aria-label={`Jump to ${b.label}`}
            >
              <MealIcon id={b.id} />
              <span className="font-mono text-[9px] uppercase tracking-wider text-fg-secondary group-hover:text-fg-primary">
                {b.label}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function MealIcon({ id }) {
  // Monochrome glyphs sized to the existing iconography in BottomTabBar
  // (22px). Coffee = cup with steam; Breakfast = sunrise plate; Lunch =
  // sandwich; Dinner = fork+knife (shared with the bottom-nav Meals tab
  // glyph but at smaller scale).
  const props = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className: 'text-fg-secondary group-hover:text-action',
    'aria-hidden': true,
  };
  if (id === 'coffee') {
    return (
      <svg {...props}>
        <path d="M5 9h12v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4z" />
        <path d="M17 11h2a2 2 0 1 1 0 4h-2" />
        <path d="M8 4c-.5 1.2.5 2 0 3.2M12 4c-.5 1.2.5 2 0 3.2" />
      </svg>
    );
  }
  if (id === 'breakfast') {
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="3.5" />
        <path d="M12 6V4M12 20v-2M6 12H4M20 12h-2M7 7l-1.4-1.4M18.4 18.4 17 17M7 17l-1.4 1.4M18.4 5.6 17 7" />
      </svg>
    );
  }
  if (id === 'lunch') {
    return (
      <svg {...props}>
        <path d="M3 10h18l-1.5 8a2 2 0 0 1-2 1.6H6.5a2 2 0 0 1-2-1.6z" />
        <path d="M3 10c0-2 4-4 9-4s9 2 9 4" />
        <path d="M7 14h10" />
      </svg>
    );
  }
  // dinner
  return (
    <svg {...props}>
      <path d="M7 3v9M7 12a2 2 0 1 1 0-4M7 12v9" />
      <path d="M16 3v18M14 3v6a2 2 0 0 0 4 0V3" />
    </svg>
  );
}

function BucketCard({
  bucket,
  anchorId,
  resolved,
  onPickSlot,
  players,
  votes,
  meId,
  pending,
  onVote,
  onResolve,
  now,
  tz,
}) {
  const { restaurants: candidates, slots: bucketSlots, activeSlot, allResolved } = bucket;
  const tally = useMemo(() => tallyVotes(votes), [votes]);
  const leader = tally.leader;
  const slotId = activeSlot?.id;

  return (
    <li id={anchorId} className="card scroll-mt-20 overflow-hidden">
      <header className="space-y-2 border-b border-border-tertiary bg-bg-secondary px-4 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="font-serif text-lg">{bucket.label}</h2>
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {candidates.length} option{candidates.length === 1 ? '' : 's'}
          </p>
        </div>
        {bucketSlots.length > 0 && !allResolved && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              voting for
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {bucketSlots.map((s) => {
                const isResolved = !!resolved[s.id]?.option_id;
                const isActive = s.id === slotId;
                const label = `${s.day_label.split(' · ')[0]} · ${s.starts_at_local}`;
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={isResolved}
                    onClick={() => onPickSlot(s.id)}
                    className={[
                      'rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition disabled:opacity-40',
                      isActive
                        ? 'border-action bg-action text-action-fg'
                        : 'border-border-tertiary bg-bg-primary text-fg-secondary hover:bg-bg-tertiary',
                      isResolved ? 'line-through' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {label}
                    {isResolved && <span className="ml-1">eaten</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {allResolved && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-success">
            every {bucket.label.toLowerCase()} slot is eaten — voting disabled
          </p>
        )}
      </header>
      <ul className="divide-y divide-border-tertiary">
        {candidates.map((r) => {
          const count = tally.byOption[r.id] || 0;
          const myVote = meId ? votes[meId] === r.id : false;
          const voters = (tally.votersByOption[r.id] || [])
            .map((pid) => playerName(players, pid))
            .join(' · ');
          const isLeading = leader.optionId === r.id && leader.count > 0;
          return (
            <li key={r.id} className="px-4 py-3">
              <div className="flex items-baseline justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-fg-primary">
                    {r.name}
                    <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                      {r.price_tier}
                    </span>
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                    {(r.cuisine || []).join(' · ')} · {r.neighborhood}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {isLeading && (
                    <span className="rounded-full border border-action/40 bg-action/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-action">
                      leading
                    </span>
                  )}
                  <span className="font-mono text-xs text-fg-secondary">
                    {count} / {players.length}
                  </span>
                </div>
              </div>

              <RestaurantBody restaurant={r} now={now} tz={tz} />

              {voters && (
                <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  votes ·{' '}
                  <span className="normal-case tracking-normal text-fg-secondary">{voters}</span>
                </p>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={!meId || pending || !slotId || allResolved}
                  onClick={() => slotId && onVote(slotId, r.id)}
                  className={[
                    'rounded-md border px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition disabled:opacity-50',
                    myVote
                      ? 'border-action bg-action text-action-fg'
                      : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
                  ].join(' ')}
                >
                  {myVote ? 'your vote ✓' : 'vote'}
                </button>
                <button
                  type="button"
                  disabled={!meId || pending || !slotId || allResolved}
                  onClick={() => slotId && onResolve(slotId, r.id)}
                  className="rounded-md border border-copper/60 bg-copper/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-copper-dark transition hover:bg-copper/20 disabled:opacity-50"
                >
                  mark eaten
                </button>
              </div>
            </li>
          );
        })}
        {candidates.length === 0 && (
          <li className="px-4 py-3 text-sm italic text-fg-tertiary">
            No candidates open right now (filter on).
          </li>
        )}
      </ul>
    </li>
  );
}

// Shared body — chips + Wilman intro. `compact` collapses the why line
// (saves vertical space in By-Type view).
function RestaurantBody({ restaurant, now, tz, compact = false }) {
  const status = openStatus(restaurant.hours_structured, now, tz);
  const cats = Array.isArray(restaurant.category)
    ? restaurant.category
    : [restaurant.category].filter(Boolean);

  return (
    <>
      {compact && (
        <header className="border-b border-border-tertiary px-3 pb-2 pt-3">
          <p className="font-medium text-fg-primary">
            {restaurant.name}
            <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              {restaurant.price_tier}
            </span>
          </p>
          {((restaurant.cuisine?.length || 0) > 0 || restaurant.neighborhood) && (
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              {(restaurant.cuisine || []).join(' · ')}
              {(restaurant.cuisine?.length || 0) > 0 && restaurant.neighborhood ? ' · ' : ''}
              {restaurant.neighborhood || ''}
            </p>
          )}
        </header>
      )}
      <div className={`mt-1 flex flex-wrap items-center gap-1.5 ${compact ? 'px-3 pb-1 pt-3' : ''}`}>
        <span
          className={[
            'rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider',
            status.open
              ? 'border-success/40 bg-success/10 text-success'
              : 'border-fg-tertiary/30 bg-bg-secondary text-fg-tertiary',
          ].join(' ')}
        >
          {status.label}
        </span>
        {cats.map((c) => (
          <span
            key={c}
            className="rounded-full border border-border-tertiary bg-bg-secondary px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary"
          >
            {c.replace(/_/g, ' ')}
          </span>
        ))}
        {restaurant.vegetarian_rating && (
          <span
            className={[
              'rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider',
              VEG_TONE[restaurant.vegetarian_rating] || '',
            ].join(' ')}
          >
            veg · {restaurant.vegetarian_rating}
          </span>
        )}
        {restaurant.decaf_available && (
          <span className="rounded-full border border-border-tertiary bg-bg-secondary px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary">
            decaf
          </span>
        )}
        {restaurant.mocktail_program && (
          <span className="rounded-full border border-border-tertiary bg-bg-secondary px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary">
            mocktails
          </span>
        )}
        {restaurant.cash_only && (
          <span className="rounded-full border border-copper/40 bg-copper/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            cash only
          </span>
        )}
      </div>

      {!compact && restaurant.why && (
        <p className="mt-2 text-sm text-fg-secondary">
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            why ·{' '}
          </span>
          {restaurant.why}
        </p>
      )}
      {compact && restaurant.why && (
        <p className="mt-1 px-3 pb-2 text-sm text-fg-secondary">{restaurant.why}</p>
      )}

      {restaurant.wilman_intro_audio_url && (
        <div className={compact ? 'px-3 pb-2' : 'mt-2'}>
          <audio
            controls
            preload="none"
            src={restaurant.wilman_intro_audio_url}
            className="h-8 w-full"
            aria-label={`Mr. Wilman on ${restaurant.name}`}
          />
        </div>
      )}
      {!restaurant.wilman_intro_audio_url && restaurant.wilman_intro_text && (
        <p className={[
          compact ? 'px-3 pb-2' : 'mt-2',
          'font-mono text-[10px] uppercase tracking-wider text-fg-tertiary',
        ].join(' ')}>
          wilman audio · pending render
        </p>
      )}
    </>
  );
}

function ArchivedSlot({ slot, restaurants, resolved, onUndo, canUndo }) {
  const r = restaurants.find((x) => x.id === resolved?.option_id);
  return (
    <li className="card px-4 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {slot.day_label} · {slot.meal_label}
          </p>
          <p className="font-medium text-fg-primary">
            {r?.name || resolved?.option_id || 'eaten'}
          </p>
        </div>
        <button
          type="button"
          disabled={!canUndo}
          onClick={onUndo}
          className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline disabled:opacity-50"
        >
          undo
        </button>
      </div>
    </li>
  );
}

function tallyVotes(votes) {
  const byOption = {};
  const votersByOption = {};
  for (const [pid, oid] of Object.entries(votes)) {
    byOption[oid] = (byOption[oid] || 0) + 1;
    if (!votersByOption[oid]) votersByOption[oid] = [];
    votersByOption[oid].push(pid);
  }
  let leader = { optionId: null, count: 0 };
  for (const [oid, c] of Object.entries(byOption)) {
    if (c > leader.count) leader = { optionId: oid, count: c };
  }
  return { byOption, votersByOption, leader };
}

function playerName(players, pid) {
  const p = players.find((pl) => pl.id === pid);
  return p?.display_name || p?.name || pid;
}
